import { useDispatch } from "react-redux";
import {
  AnyAction,
  ThunkAction,
  ThunkDispatch,
  combineReducers,
  configureStore,
  createListenerMiddleware,
} from "@reduxjs/toolkit";

import { logoutSuccess } from "./authentication/store/auth.slice";
import { appUsersApiSlice } from "./modules/app-users/store/app-users.rtkq";
import { assetStocksApiSlice } from "./modules/asset-stocks/store";
import { chemicalsApiSlice } from "./modules/chemicals/store/chemicals.rtkq";
import { cropsApiSlice } from "./modules/crop/store/crops.rtkq";
import { cropTypesApi } from "./modules/crop-types/store/crop-types.rtkq";
import { diseasesApiSlice } from "./modules/diseases/store/diseases.rtkq";
import { materiallyAccountableEmployeesSlice } from "./modules/employees/store";
import { employeesApiSlice } from "./modules/employees/store/employees.rtkq";
import { currentFarmLandsApiSlice } from "./modules/farm-lands/current-farm-lands.rtkq";
import { farmsApi } from "./modules/farms/store/farms.rtkq";
import { fertilisersApiSlice } from "./modules/fertilisers/store/fertilisers.rtkq";
import { departmentsApi } from "./modules/info-data/store/departments.rtkq";
import { measuresApi } from "./modules/info-data/store/measures.rtkq";
import { positionsApi } from "./modules/info-data/store/positions.rtkq";
import { seasonsApi } from "./modules/info-data/store/seasons.rtkq";
import { techOperationGroupsApi } from "./modules/info-data/store/tech-operation-groups.rtkq";
import { techOperationScalarsApi } from "./modules/info-data/store/tech-operation-scalars.rtkq";
import { techOperationSubGroupsApi } from "./modules/info-data/store/tech-operation-subgroups.rtkq";
import { techOperationMeasuresSetsApi } from "./modules/info-data/store/tech-operations-measures-sets.rtkq";
import { techOperationParamsApi } from "./modules/info-data/store/tech-operations-params.rtkq";
import { workPlacesApi } from "./modules/info-data/store/workplaces.rtkq";
import { pestsApiSlice } from "./modules/pests/store/pests.rtkq";
import { phenophasesApi } from "./modules/phenophases/store/phenophases.rtkq";
import { seedsApiSlice } from "./modules/seeds/store/seeds.rtkq";
import { techAssetsApiSlice } from "./modules/tech-assets/store/techop-assets.rtkq";
import { weedsApiSlice } from "./modules/weeds/store/weeds.rtkq";
import { assetStockBalancesApiSlice } from "./pages/asset-stocks/store/asset-stocks.rtkq";
import { assetUsageRecordsApi } from "./pages/asset-usage-records/store/asset-usage-records.rtkq";
import { weatherApi } from "./pages/fields/store/farm-lands-weather.rtkq";
import { farmLandsApi } from "./pages/fields/store/farm-lands.rtkq";
import { mapPageApi } from "./pages/map-page/store/map-page.rtkq";
import { observationsApiSlice } from "./pages/observations/store/observations.rtkq";
import { techopsRelatedApiSlice } from "./pages/tech-operations/store/techop-related.rtkq";
import { techopsApiSlice } from "./pages/tech-operations/store/techop.rtkq";
import { techWorksApiSlice } from "./pages/tech-works/store/tech-works.rtkq";
import { dynamicReducers } from "./reducers";

// Функция, не объект: в тестах циклические импорты могут дойти сюда раньше reducers.ts, dynamicReducers будет undefined.
export const createRootReducer = () => ({
  ...dynamicReducers,
  [techopsApiSlice.reducerPath]: techopsApiSlice.reducer,
  [techopsRelatedApiSlice.reducerPath]: techopsRelatedApiSlice.reducer,
  [currentFarmLandsApiSlice.reducerPath]: currentFarmLandsApiSlice.reducer,
  [techAssetsApiSlice.reducerPath]: techAssetsApiSlice.reducer,
  [materiallyAccountableEmployeesSlice.reducerPath]:
    materiallyAccountableEmployeesSlice.reducer,
  [assetStocksApiSlice.reducerPath]: assetStocksApiSlice.reducer,
  [assetStockBalancesApiSlice.reducerPath]: assetStockBalancesApiSlice.reducer,
  [assetUsageRecordsApi.reducerPath]: assetUsageRecordsApi.reducer,
  [farmLandsApi.reducerPath]: farmLandsApi.reducer,
  [mapPageApi.reducerPath]: mapPageApi.reducer,
  [observationsApiSlice.reducerPath]: observationsApiSlice.reducer,
  [techWorksApiSlice.reducerPath]: techWorksApiSlice.reducer,
  [employeesApiSlice.reducerPath]: employeesApiSlice.reducer,
  [seedsApiSlice.reducerPath]: seedsApiSlice.reducer,
  [appUsersApiSlice.reducerPath]: appUsersApiSlice.reducer,
  [chemicalsApiSlice.reducerPath]: chemicalsApiSlice.reducer,
  [diseasesApiSlice.reducerPath]: diseasesApiSlice.reducer,
  [fertilisersApiSlice.reducerPath]: fertilisersApiSlice.reducer,
  [pestsApiSlice.reducerPath]: pestsApiSlice.reducer,
  [weedsApiSlice.reducerPath]: weedsApiSlice.reducer,
  [cropsApiSlice.reducerPath]: cropsApiSlice.reducer,
  [weatherApi.reducerPath]: weatherApi.reducer,
  [cropTypesApi.reducerPath]: cropTypesApi.reducer,
  [phenophasesApi.reducerPath]: phenophasesApi.reducer,
  [farmsApi.reducerPath]: farmsApi.reducer,
  [departmentsApi.reducerPath]: departmentsApi.reducer,
  [workPlacesApi.reducerPath]: workPlacesApi.reducer,
  [seasonsApi.reducerPath]: seasonsApi.reducer,
  [positionsApi.reducerPath]: positionsApi.reducer,
  [techOperationGroupsApi.reducerPath]: techOperationGroupsApi.reducer,
  [techOperationSubGroupsApi.reducerPath]: techOperationSubGroupsApi.reducer,
  [techOperationScalarsApi.reducerPath]: techOperationScalarsApi.reducer,
  [techOperationMeasuresSetsApi.reducerPath]: techOperationMeasuresSetsApi.reducer,
  [techOperationParamsApi.reducerPath]: techOperationParamsApi.reducer,
  [measuresApi.reducerPath]: measuresApi.reducer,
});

export type RootState = ReturnType<
  ReturnType<typeof combineReducers<ReturnType<typeof createRootReducer>>>
>;

const appQueryApis = [
  techopsApiSlice,
  techopsRelatedApiSlice,
  currentFarmLandsApiSlice,
  techAssetsApiSlice,
  materiallyAccountableEmployeesSlice,
  assetStocksApiSlice,
  assetStockBalancesApiSlice,
  assetUsageRecordsApi,
  farmLandsApi,
  mapPageApi,
  observationsApiSlice,
  techWorksApiSlice,
  employeesApiSlice,
  seedsApiSlice,
  appUsersApiSlice,
  chemicalsApiSlice,
  diseasesApiSlice,
  fertilisersApiSlice,
  pestsApiSlice,
  weedsApiSlice,
  cropsApiSlice,
  phenophasesApi,
  weatherApi,
  cropTypesApi,
  farmsApi,
  positionsApi,
  seasonsApi,
  workPlacesApi,
  departmentsApi,
  techOperationGroupsApi,
  techOperationSubGroupsApi,
  techOperationScalarsApi,
  measuresApi,
  techOperationMeasuresSetsApi,
  techOperationParamsApi,
];

export const appQueryMiddleware = appQueryApis.map((api) => api.middleware);

const logoutListenerMiddleware = createListenerMiddleware<RootState>();
logoutListenerMiddleware.startListening({
  matcher: logoutSuccess.match,
  effect: (_, { dispatch }) => {
    appQueryApis.forEach((api) => dispatch(api.util.resetApiState()));
  },
});

export const createAppStore = (preloadedState?: Partial<RootState>) =>
  configureStore({
    devTools: { trace: true, maxAge: 20 },
    reducer: combineReducers(createRootReducer()),
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: false,
      })
        .prepend(logoutListenerMiddleware.middleware)
        .concat(...appQueryMiddleware),
  });

export const store = createAppStore();

farmsApi.endpoints.fetchFarms.initiate();

export type AppDispatch = ThunkDispatch<RootState, undefined, AnyAction>;

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  AnyAction
>;

// Export a hook that can be reused to resolve types
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>();
